/** Scope 3's band - ADR-0212, package P9.
 *
 * - `Scope3CategoryCard`: the categories that **have a figure**, ranked, from the inventory's own
 *   category totals (`by_category`, scope 3). A category nobody has priced is not a row: the engine
 *   prices few Scope 3 categories today, and a bar for one that has no figure would be a zero. The
 *   fifteen-category assessment is the Categories tab, which says "not assessed" in words.
 * - `Scope3GradeCard`: how the figures were reached, as counts of figures per grade. The current grade
 *   is the one most figures sit at (the weaker on a tie), a statement about the mix and not a grade
 *   the server gave the total. The counts are the server's (`by_category`'s `grade_counts`), so they
 *   are the whole window however many figures it holds.
 */

import { GradeMeter, HBarList } from "../../components/charts";
import type { GradeSegment, HBarItem } from "../../components/charts";
import type { Inventory } from "../../api/queries";
import { EmptyState } from "../../components/ui";
import { share, tonnes } from "../../lib/format";
import { gradeCountsOf, words } from "../scopeTable/model";
import { BandCard } from "./BandCard";
import type { BandRead } from "./BandCard";
import { geometryTonnes } from "./bandModel";

export function Scope3CategoryCard({
  read,
  inventory,
  onOpen,
}: {
  read: BandRead;
  inventory: Inventory | undefined;
  /** Opens the figures of a category; called with `category-<engine category>`. */
  onOpen: (key: string) => void;
}) {
  const card = inventory?.totals_by_scope.find((candidate) => candidate.scope === "3");
  const rows = (inventory?.by_category ?? [])
    .filter((row) => row.scope === "3" && Number(row.kgco2e) !== 0)
    .sort((a, b) => Number(b.kgco2e) - Number(a.kgco2e));
  const items: HBarItem[] = rows.map((row) => ({
    key: row.category,
    label: words(row.category),
    value: Math.max(0, geometryTonnes(row.kgco2e)),
    text: `${tonnes(row.kgco2e)} t`,
    ...(card?.assessed ? { note: share(row.kgco2e, card.kgco2e) ?? undefined } : {}),
    receipt: `category-${row.category}`,
  }));
  return (
    <BandCard title="Scope 3 by category" unit="tCO2e" reads={[read]} testId="band-scope3-categories">
      {items.length > 0 ? (
        <HBarList
          items={items}
          caption="Scope 3 by category"
          unit="tCO2e"
          colour="scope3"
          onOpenReceipt={onOpen}
          testId="band-scope3-list"
        />
      ) : (
        <EmptyState title="No Scope 3 figures in this period" compact />
      )}
    </BandCard>
  );
}

const GRADE_WORDS: Record<"A" | "B" | "C" | "D", string> = {
  A: "Traced to a bill",
  B: "Recorded by a person, or a split",
  C: "An estimate",
  D: "Lowest grade",
};
const ORDER = ["A", "B", "C", "D"] as const;

export function Scope3GradeCard({
  read,
  inventory,
  onOpen,
}: {
  read: BandRead;
  inventory: Inventory | undefined;
  /** Opens the figures at grade A; called with `grade-a`. */
  onOpen: (key: string) => void;
}) {
  const counts = gradeCountsOf(inventory, "3");
  const shown = counts && counts.D > 0 ? ORDER : (["A", "B", "C"] as const);
  const total = counts ? counts.A + counts.B + counts.C + counts.D : 0;
  const current = counts
    ? [...shown].reverse().reduce((best, grade) => (counts[grade] > counts[best] ? grade : best), shown[shown.length - 1] ?? "C")
    : "C";
  const grades: GradeSegment[] = counts
    ? shown.map((grade) => ({
        grade,
        text: GRADE_WORDS[grade],
        figure: `${String(counts[grade])} figure${counts[grade] === 1 ? "" : "s"}`,
        ...(grade === "A" && counts.A > 0 ? { receipt: "grade-a" } : {}),
      }))
    : [];
  return (
    <BandCard title="Scope 3 by grade" unit="figures" reads={[read]} testId="band-scope3-grades">
      {counts && total > 0 ? (
        <GradeMeter grades={grades} current={current} caption="Scope 3 figures by grade" onOpenReceipt={onOpen} testId="band-scope3-meter" />
      ) : (
        <EmptyState title="No Scope 3 figures in this period" compact />
      )}
    </BandCard>
  );
}
